import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories, comics } from '@/mocks/comics';

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '韩国漫画分类 - 爱漫画',
  description: '浏览爱漫画全部韩国漫画分类，包含恋爱、奇幻、动作、悬疑、搞笑等多种类型无删减韩漫。',
  url: 'https://aimanhua.com/category',
  isPartOf: {
    '@type': 'WebSite',
    name: '爱漫画',
    url: 'https://aimanhua.com/',
  },
};

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const [filteredComics, setFilteredComics] = useState(comics);

  useEffect(() => {
    if (activeCat === 'all') {
      setFilteredComics(comics);
    } else {
      setFilteredComics(comics.filter(c => c.categories.includes(activeCat)));
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeCat]);

  const handleCategoryChange = (catId: string) => {
    setSearchParams(catId === 'all' ? {} : { cat: catId });
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览' },
  ];

  return (
    <>
      <SEOHead
        title="漫画分类"
        description="浏览爱漫画全部韩国漫画分类，包含恋爱、奇幻、动作、悬疑、搞笑、校园、职场等多种类型无删减韩漫，下拉式免费在线阅读。"
        keywords="韩国漫画分类,无删减韩漫类型,恋爱韩漫,奇幻韩漫,动作韩漫"
        canonicalUrl="https://aimanhua.com/category"
        schemaJson={pageSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                漫画分类
              </h1>
              <p className="text-sm text-foreground-500">探索您喜爱的韩国漫画类型，发现更多精彩作品</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCat === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 pb-16">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 flex items-center justify-center text-foreground-300 mb-4">
                  <i className="ri-emotion-sad-line text-5xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}